(() => {
  'use strict';

  if (!('customElements' in window) || customElements.get('cs-home-feed')) return;

  class CsHomeFeed extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.restorePendingAnnouncement();
        this.restorePendingFocus();
        this.connectScrollListener();
        this.refreshObservation();
        return;
      }
      this.initialized = true;

      this.grid = this.querySelector('[data-feed-grid]');
      this.sentinel = this.querySelector('[data-feed-sentinel]');
      this.controls = this.querySelector('[data-feed-controls]');
      this.moreButton = this.querySelector('[data-feed-more]');
      this.allLink = this.querySelector('[data-feed-all]');
      this.status = this.querySelector('[data-feed-status]');
      this.messages = this.readMessages();
      this.perPage = this.numberFrom(this.dataset.perPage, 12);
      this.maxPages = this.numberFrom(this.dataset.maxPages, 5);
      this.currentPage = this.numberFrom(this.dataset.currentPage, 1);
      this.pagesLoaded = 1;
      this.loading = false;
      this.finished = false;
      // r64（Codex 選項 B）：自動載入的卡片回來時 controls 已進入視窗，就先放這裡、不插入（插入會把 controls 與下方區塊推走＝CLS）；下次載入（按鈕或再次符合安全位置的自動載入）先消耗它
      this.pendingCards = [];
      this.automatic = 'IntersectionObserver' in window;
      this.userScrolled = false;
      this.suppressScroll = false;
      this.scrollFrame = null;
      this.scrollListening = false;
      this.initialHasNext = this.dataset.hasNext === 'true';
      this.deferInitial = this.dataset.deferInitial === 'true';
      if (this.deferInitial) this.pagesLoaded = 0;
      this.excludedHandles = new Set((this.dataset.excludeHandles || '').split('|').filter(Boolean));
      this.source = this.createSource(this.deferInitial ? 'best-selling' : 'default');
      this.source.exhausted = !this.deferInitial && !this.initialHasNext;

      if (!this.grid || !this.sentinel || !this.controls || !this.moreButton || !this.allLink || !this.status) return;

      this.onWindowScroll = () => this.handleScroll();
      this.moreButton.addEventListener('click', () => this.onMore());
      this.deduplicateInitialGrid();
      this.connectScrollListener();
      this.start();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      this.disconnectScrollListener();
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }

    async start() {
      if (this.deferInitial) {
        this.setLoading(true);
        try {
          const cards = await this.takeCards(this.perPage, this.source, true);
          const uniqueCards = this.filterAgainstMain(cards, true);
          this.lazyFrom(uniqueCards, 8);
          this.grid.replaceChildren(...uniqueCards);
          this.pagesLoaded = 1;
          this.setLoading(false);
          this.status.textContent = '';
          if (!uniqueCards.length || this.reachedLimit() || !this.sourceHasMore()) {
            this.finish();
          } else {
            this.refreshObservation();
          }
        } catch (_error) {
          this.setLoading(false);
          this.fail({ showAllLink: true });
        }
        return;
      }

      let sorted = false;

      if (this.canReplaceBelowViewport()) {
        this.setLoading(true);
        try {
          sorted = await this.replaceFirstPageWithBestSelling();
        } catch (_error) {
          this.setLoading(false);
          if (this.reachedLimit() || !this.sourceHasMore()) {
            this.finish();
            return;
          }
          this.fail();
          return;
        }
        this.setLoading(false);
        this.status.textContent = '';
      }

      if (!sorted && !this.initialHasNext) {
        this.finish();
        return;
      }

      if (this.reachedLimit() || !this.sourceHasMore()) {
        this.finish();
        return;
      }

      this.refreshObservation();
    }

    createSource(sort) {
      return {
        sort,
        nextPage: 1,
        buffer: [],
        exhausted: false,
        totalPages: null
      };
    }

    numberFrom(value, fallback) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    canReplaceBelowViewport() {
      return this.getBoundingClientRect().top >= window.innerHeight;
    }

    deduplicateInitialGrid() {
      const cards = Array.from(this.grid.children);
      const seen = this.handlesBeforeGrid();
      const uniqueCards = cards.filter((card) => {
        const handle = this.productHandle(card);
        if (!handle || seen.has(handle) || this.excludedHandles.has(handle)) return false;
        seen.add(handle);
        return true;
      });

      if (uniqueCards.length !== cards.length) this.grid.replaceChildren(...uniqueCards);
    }

    handlesBeforeGrid() {
      const handles = new Set();
      const main = this.closest('main') || document.querySelector('main');
      if (!main) return handles;

      main.querySelectorAll('a[href]').forEach((link) => {
        if (!(link.compareDocumentPosition(this.grid) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
        const handle = this.handleFromHref(link.getAttribute('href'));
        if (handle) handles.add(handle);
      });

      return handles;
    }

    async replaceFirstPageWithBestSelling() {
      const source = this.createSource('best-selling');
      const cards = await this.takeCards(this.perPage, source, true);

      if (!cards.length || !this.canReplaceBelowViewport()) return false;

      const uniqueCards = this.filterAgainstMain(cards, true);
      if (!uniqueCards.length) return false;

      this.grid.replaceChildren(...uniqueCards);
      this.source = source;
      return true;
    }

    observe() {
      if (!this.automatic || !this.userScrolled || this.finished || this.observer || !this.isConnected) return;

      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.maybeAutoLoad();
      }, { rootMargin: '0px 0px 600px 0px' });
      this.observer.observe(this.sentinel);
    }

    connectScrollListener() {
      if (!this.automatic || this.finished || this.scrollListening || !this.onWindowScroll) return;
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      this.scrollListening = true;
    }

    disconnectScrollListener() {
      if (!this.scrollListening || !this.onWindowScroll) return;
      window.removeEventListener('scroll', this.onWindowScroll);
      this.scrollListening = false;
    }

    handleScroll() {
      if (this.suppressScroll) return;
      this.userScrolled = true;
      if (this.scrollFrame !== null) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.observe();
        this.maybeAutoLoad();
      });
    }

    maybeAutoLoad() {
      if (!this.canAutoLoadNow()) return;
      this.loadNext('auto');
    }

    // r38：首頁 ≥990 首批只顯示前 8 張（CSS 藏 9 張起）；按「看更多」第一下先展開，不聽捲動。
    foldActive() {
      return this.classList.contains('cs-feed-fold') && this.grid.children.length > 8 && window.matchMedia('(min-width: 990px)').matches;
    }

    onMore() {
      if (this.foldActive()) {
        this.classList.remove('cs-feed-fold');
        if (this.finished) {
          this.moreButton.hidden = true;
          this.allLink.hidden = false;
        }
        // 換狀態文字：讀屏報數，也讓 cs-g5b-picks.js 的 trimTail（監看 status）依展開後的張數重算尾列
        // r60 Codex #19：來源已耗盡（finished）時，展開這一下才是真的全部顯示，這時才報 loadedAll；否則照舊報 loaded
        // r62：到了頁數上限（reachedLimit）但來源還有貨（sourceDone false）時，不是全部，只報 loaded
        this.announce(this.finished && this.sourceDone ? this.msg('loadedAll', this.grid.children.length) : this.msg('loaded', this.grid.children.length));
        this.focusFirstCard([this.grid.children[8]]);
        return;
      }
      this.classList.remove('cs-feed-fold');
      this.loadNext('button');
    }

    lazyFrom(cards, start) {
      cards.slice(start).forEach((card) => {
        card.querySelectorAll('img').forEach((img) => { img.loading = 'lazy'; });
      });
    }

    canAutoLoadNow() {
      if (this.foldActive()) return false;
      if (!this.automatic || !this.userScrolled || this.loading || this.finished || !this.isConnected) return false;
      if (this.reachedLimit() || !this.sourceHasMore()) return false;

      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      const controlsRect = this.controls.getBoundingClientRect();
      const sentinelRect = this.sentinel.getBoundingClientRect();

      if (controlsRect.top < viewportHeight) return false;
      return sentinelRect.bottom >= 0 && sentinelRect.top <= viewportHeight + 600;
    }

    async loadNext(trigger = 'button') {
      if (this.loading || this.finished) return;
      const moveFocus = trigger === 'button';
      const controlsTop = moveFocus ? this.visibleControlsTop() : null;

      if (this.reachedLimit() || !this.sourceHasMore()) {
        this.finish({ moveFocus });
        return;
      }

      this.setLoading(true);
      try {
        // r64：有暫存的卡就先用暫存（不再抓），避免漏掉或重複
        let cards;
        if (this.pendingCards && this.pendingCards.length) {
          cards = this.pendingCards;
          this.pendingCards = [];
        } else {
          cards = await this.takeCards(this.perPage, this.source, false);
        }
        const uniqueCards = this.filterAgainstMain(cards, false);

        // r64：自動載入在等待期間 controls 可能已捲進視窗；這時不插入，暫存起來、解除 loading，留「看更多」給使用者接續。不宣告任何狀態（不可說 loadedAll）
        if (!moveFocus && uniqueCards.length && !this.controlsBelowViewport()) {
          this.pendingCards = uniqueCards;
          this.setLoading(false);
          this.status.textContent = '';
          this.moreButton.hidden = false;
          this.moreButton.disabled = false;
          return;
        }

        if (uniqueCards.length) {
          if (moveFocus) this.suppressManagedScroll();
          this.grid.append(...uniqueCards);
          this.restoreControlsTop(controlsTop);
          this.pagesLoaded += 1;
        }

        this.setLoading(false);

        if (this.reachedLimit() || !this.sourceHasMore()) {
          this.finish({ moveFocus, loadedCount: uniqueCards.length, preventScroll: controlsTop !== null });
          return;
        }

        this.announce(this.msg('loaded', uniqueCards.length));
        if (moveFocus) {
          this.userScrolled = false;
          this.focusFirstCard(uniqueCards);
        }
      } catch (_error) {
        this.setLoading(false);
        if (this.reachedLimit()) {
          this.finish({ moveFocus });
          return;
        }
        this.fail();
        return;
      }
      this.refreshObservation();
    }

    controlsBelowViewport() {
      if (!this.isConnected) return false;
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      return this.controls.getBoundingClientRect().top >= viewportHeight;
    }

    visibleControlsTop() {
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      const rect = this.controls.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < viewportHeight ? rect.top : null;
    }

    restoreControlsTop(previousTop) {
      if (previousTop === null || !this.isConnected) return;
      const difference = this.controls.getBoundingClientRect().top - previousTop;
      if (Math.abs(difference) > 0.5) window.scrollBy(0, difference);
    }

    suppressManagedScroll() {
      this.suppressScroll = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.suppressScroll = false;
        });
      });
    }

    async takeCards(count, source, excludeOwnGrid) {
      const cards = [];
      const seen = this.handlesInMain(excludeOwnGrid);
      let pagesFetched = 0;

      try {
        while (cards.length < count) {
          while (source.buffer.length && cards.length < count) {
            const card = source.buffer.shift();
            const handle = this.productHandle(card);
            if (!handle || seen.has(handle) || this.excludedHandles.has(handle)) continue;
            seen.add(handle);
            cards.push(card);
          }

          if (cards.length >= count || source.exhausted) break;
          if (pagesFetched >= this.maxPages) {
            source.exhausted = true;
            // r62：這裡是「一次抓了上限頁數還湊不滿」的放棄，不是來源真的沒貨；finish 據此不說「已全部顯示」
            source.gaveUp = true;
            break;
          }
          await this.fetchSourcePage(source);
          pagesFetched += 1;
        }
      } catch (error) {
        source.buffer.unshift(...cards);
        throw error;
      }

      return cards;
    }

    async fetchSourcePage(source) {
      const page = source.nextPage;
      const url = this.buildUrl(page, source.sort);
      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const renderedSection = documentFragment.querySelector('cs-home-feed');
      const renderedGrid = renderedSection && renderedSection.querySelector('[data-feed-grid]');

      if (!renderedSection || !renderedGrid) throw new Error('Section response missing');

      const currentPage = this.numberFrom(renderedSection.dataset.currentPage, 0);
      const totalPages = this.numberFrom(renderedSection.dataset.totalPages, 1);
      const renderedPerPage = this.numberFrom(renderedSection.dataset.perPage, 12);
      if (currentPage !== page || renderedPerPage < 1) throw new Error('Section page mismatch');

      // V16 r25：首頁已在貨架前輸出一次卡片 CSS（style[data-cs-card-css="once"]），抓回的卡各自帶的那份就不插進頁面。
      if (document.querySelector('style[data-cs-card-css="once"]')) {
        renderedGrid.querySelectorAll('style[data-cs-card-css]').forEach((style) => style.remove());
      }
      source.buffer.push(...Array.from(renderedGrid.children));
      source.totalPages = totalPages;
      source.nextPage = page + 1;
      source.exhausted = renderedSection.dataset.hasNext !== 'true' || source.nextPage > totalPages;
    }

    buildUrl(page, sort) {
      const url = new URL(this.dataset.collectionUrl, document.baseURI);
      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', this.dataset.renderSection || 'cs-home-feed');
      if (this.dataset.view) url.searchParams.set('view', this.dataset.view);

      if (sort === 'best-selling') {
        url.searchParams.set('sort_by', 'best-selling');
      } else {
        url.searchParams.delete('sort_by');
      }

      const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewThemeId !== null) url.searchParams.set('preview_theme_id', previewThemeId);

      return url;
    }

    productHandle(root) {
      if (root.matches && root.matches('a[href]')) {
        const ownHandle = this.handleFromHref(root.getAttribute('href'));
        if (ownHandle) return ownHandle;
      }

      const links = root.querySelectorAll('a[href]');

      for (const link of links) {
        const handle = this.handleFromHref(link.getAttribute('href'));
        if (handle) return handle;
      }

      return null;
    }

    handleFromHref(href) {
      try {
        const path = new URL(href, document.baseURI).pathname;
        const match = path.match(/\/products\/([^/]+)/);
        if (!match) return null;

        try {
          return decodeURIComponent(match[1]);
        } catch (_error) {
          return match[1];
        }
      } catch (_error) {
        return null;
      }
    }

    handlesInMain(excludeOwnGrid) {
      const handles = new Set();
      const main = this.closest('main') || document.querySelector('main');
      if (!main) return handles;

      main.querySelectorAll('a[href]').forEach((link) => {
        if (excludeOwnGrid && this.grid.contains(link)) return;
        const handle = this.handleFromHref(link.getAttribute('href'));
        if (handle) handles.add(handle);
      });

      main.querySelectorAll('[data-cs-product-handle]').forEach((element) => {
        if (excludeOwnGrid && this.grid.contains(element)) return;
        const handle = element.dataset.csProductHandle;
        if (handle) handles.add(handle);
      });

      return handles;
    }

    filterAgainstMain(cards, excludeOwnGrid) {
      const seen = this.handlesInMain(excludeOwnGrid);

      return cards.filter((card) => {
        const handle = this.productHandle(card);
        if (!handle || seen.has(handle) || this.excludedHandles.has(handle)) return false;
        seen.add(handle);
        return true;
      });
    }

    sourceHasMore() {
      // r64：暫存的卡也算「還有可載入的內容」
      return (this.pendingCards && this.pendingCards.length > 0) || this.source.buffer.length > 0 || !this.source.exhausted;
    }

    reachedLimit() {
      return this.pagesLoaded >= this.maxPages;
    }

    refreshObservation() {
      if (!this.automatic || !this.userScrolled || this.finished || !this.isConnected) return;
      this.observe();
      if (this.observer) {
        this.observer.unobserve(this.sentinel);
        this.observer.observe(this.sentinel);
      }
      this.maybeAutoLoad();
    }

    focusFirstCard(cards) {
      for (const card of cards) {
        const links = card.querySelectorAll('a[href]');
        for (const link of links) {
          if (!this.handleFromHref(link.getAttribute('href'))) continue;
          this.focusElement(link);
          return;
        }
      }

      this.focusElement(this.moreButton);
    }

    focusElement(element, options = {}) {
      if (this.isConnected) {
        element.focus(options);
        return;
      }

      this.pendingFocus = { element, options: { preventScroll: false } };
    }

    restorePendingFocus() {
      if (!this.pendingFocus || !this.isConnected) return;
      const pendingFocus = this.pendingFocus;
      this.pendingFocus = null;
      pendingFocus.element.focus(pendingFocus.options);
    }

    // r53：狀態文字與按鈕字（more／all，按鈕字由 Liquid 直接輸出，這裡只用來填 {more} 與檢查缺字）四語。字串由 sections/cs-home-feed.liquid 依 request.locale.iso_code 寫在 data-msg-*（{n}＝件數，{more}＝看更多按鈕的字）。
    // 缺哪一句就用繁中（r53 之前的字），並在元素上印 data-cs-msg-miss＝缺的鍵名，不安靜退讓。
    // cs-g5b-picks.js 的 statusFixText／trimTail 讀同一組 data-msg-* 來比對，改字串時兩邊不用各改一次。
    readMessages() {
      const fallback = {
        loaded: '已載入 {n} 件商品。',
        loadedAll: '已載入 {n} 件商品，本區載入已完成。',
        allShown: '本區載入已完成。',
        loading: '載入中…',
        failed: '載入失敗，請按「{more}」重試。',
        more: '看更多',
        all: '看全部'
      };
      const messages = {};
      const missing = [];
      Object.keys(fallback).forEach((key) => {
        const value = this.dataset['msg' + key.charAt(0).toUpperCase() + key.slice(1)];
        if (value) {
          messages[key] = value;
        } else {
          messages[key] = fallback[key];
          missing.push(key);
        }
      });
      if (missing.length) this.setAttribute('data-cs-msg-miss', missing.join(','));
      return messages;
    }

    msg(key, count = 0) {
      // {more}＝按鈕上實際顯示的字（Liquid 已依語言輸出 data-msg-more 同一句）；讀不到才用 messages.more
      const more = (this.moreButton && this.moreButton.textContent.trim()) || this.messages.more;
      return this.messages[key].split('{n}').join(String(count)).split('{more}').join(more);
    }

    announce(message) {
      if (this.isConnected) {
        this.status.textContent = message;
        return;
      }

      this.status.textContent = '';
      this.pendingAnnouncement = message;
    }

    restorePendingAnnouncement() {
      if (!this.pendingAnnouncement || !this.isConnected) return;
      const message = this.pendingAnnouncement;
      this.pendingAnnouncement = null;
      this.status.textContent = '';

      requestAnimationFrame(() => {
        if (this.pendingAnnouncement) return;
        if (this.isConnected) {
          this.status.textContent = message;
        } else {
          this.pendingAnnouncement = message;
        }
      });
    }

    setLoading(loading) {
      this.loading = loading;
      if (loading) {
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
      }
      this.moreButton.disabled = loading;
      if (loading && !this.finished) this.status.textContent = this.msg('loading');
    }

    fail({ showAllLink = false } = {}) {
      this.automatic = false;
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      this.disconnectScrollListener();
      this.moreButton.hidden = false;
      this.moreButton.disabled = false;
      if (showAllLink) this.allLink.hidden = false;
      this.announce(this.msg('failed'));
    }

    finish({ moveFocus = false, loadedCount = 0, preventScroll = false } = {}) {
      this.finished = true;
      // r62：finish 有兩種來由：來源真的耗盡（sourceHasMore false），或到了頁數上限（reachedLimit）而來源還有貨。只有前者可以說「已全部顯示」
      this.sourceDone = !this.sourceHasMore() && !this.source.gaveUp;
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      this.disconnectScrollListener();
      const folded = this.foldActive();
      // 窄螢幕載完就拿掉 fold，免得之後轉成橫向 ≥990 時第 9 張起被藏、又沒有按鈕可展開（一次性，不是捲動中反覆寫）。
      if (!folded) this.classList.remove('cs-feed-fold');
      this.moreButton.hidden = !folded;
      this.allLink.hidden = folded;
      // r60 Codex #19：還折疊著（≥990、9 張起被藏、看更多鈕還在）就不可說「已全部顯示」，只報「已載入 N 件」；展開時 onMore 再報 loadedAll
      let message;
      if (folded) message = this.msg('loaded', this.grid.children.length);
      else if (!this.sourceDone) message = this.msg('loaded', loadedCount > 0 ? loadedCount : this.grid.children.length);
      else message = loadedCount > 0 ? this.msg('loadedAll', loadedCount) : this.msg('allShown');
      this.announce(message);
      if (moveFocus) this.focusElement(this.allLink, { preventScroll });
    }
  }

  customElements.define('cs-home-feed', CsHomeFeed);
})();
